import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useNavigate } from "@tanstack/react-router"
import { SignUpOutput } from "aws-amplify/auth"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TermsAndConditions from "@/globalComponents/molecules/TermsAndConditions"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import {
  passwordValidationRule,
  requiredRule,
  validEmailRule,
} from "@/utilities/form"

export const Route = createFileRoute("/auth/create-account")({
  component: RouteComponent,
})

interface CreateAccountFormSchema {
  firstName: string
  lastName: string
  phone: string
  email: string
  password: string
  confirmPassword: string
}

// Helper function to log mutation errors to Sentry
function logMutationError(
  error: any,
  context: {
    flow: string
    email?: string
    qrLookupID?: string
    additionalData?: Record<string, any>
  }
) {
  Sentry.captureException(error, {
    extra: {
      email: context.email,
      qrLookupID: context.qrLookupID,
      originalError: error.message,
      ...context.additionalData,
    },
    tags: {
      section: "auth",
      flow: context.flow,
    },
  })
}

function RouteComponent() {
  const { qrLookupID, redirect } = Route.useSearch()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const form = useForm<CreateAccountFormSchema>({
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    mode: "onSubmit",
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <>
      <Heading level={1} as={1}>
        {t("auth.createAccount.heading")}
      </Heading>
      <Text level={2} style={{ marginTop: "var(--space-2)" }}>
        {t("auth.createAccount.description")}
      </Text>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset disabled={isPending}>
          <div className="form-row">
            <Controller<CreateAccountFormSchema>
              name="firstName"
              control={form.control}
              rules={{ required: requiredRule(t("errors.common.required")) }}
              render={({
                field: { ref, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField {...rest} isInvalid={invalid}>
                  <Label>{t("auth.createAccount.firstName")}</Label>
                  <TextField.Input innerRef={ref} placeholder="Alex" />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <Controller<CreateAccountFormSchema>
              name="lastName"
              control={form.control}
              rules={{ required: requiredRule(t("errors.common.required")) }}
              render={({
                field: { ref, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField {...rest} isInvalid={invalid}>
                  <Label>{t("auth.createAccount.lastName")}</Label>
                  <TextField.Input innerRef={ref} placeholder="Smith" />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
          </div>
          <Controller<CreateAccountFormSchema>
            name="phone"
            control={form.control}
            rules={{ required: requiredRule(t("errors.common.required")) }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>{t("auth.createAccount.phoneNumber")}</Label>
                <TextField.PhoneWithCountrySelect
                  innerRef={ref}
                  defaultCountry="US"
                  placeholder="(123) 456-6789"
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<CreateAccountFormSchema>
            name="email"
            control={form.control}
            rules={{
              required: requiredRule(t("errors.common.required")),
              pattern: validEmailRule(t("errors.common.validEmail")),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>{t("auth.createAccount.email")}</Label>
                <TextField.Input
                  innerRef={ref}
                  type="email"
                  placeholder="alex.smith@example.com"
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<CreateAccountFormSchema>
            name="password"
            control={form.control}
            rules={{
              required: requiredRule(t("errors.common.required")),
              ...passwordValidationRule(t),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>{t("auth.createAccount.password")}</Label>
                <TextField.Password innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<CreateAccountFormSchema>
            name="confirmPassword"
            control={form.control}
            rules={{
              required: requiredRule(t("errors.common.required")),
              validate: (value) => {
                const password = form.getValues("password")
                return value === password || t("errors.common.passwordMismatch")
              },
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>{t("auth.createAccount.confirmPassword")}</Label>
                <TextField.Password innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <TermsAndConditions qrLookupID={qrLookupID || ""} />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button
            hue="secondary"
            variant="default"
            type="submit"
            isPending={isPending}
          >
            {t("auth.createAccount.submitButton")}
          </Button>
        </fieldset>
      </form>
      <Text level={2} className="center">
        {t("auth.createAccount.alreadyHaveAccount")}{" "}
        <LinkButton
          hue="primary"
          presentation="link"
          style={{ textDecorationLine: "underline" }}
          to="/auth/sign-in"
          search={{ qrLookupID, redirect }}
        >
          {t("auth.createAccount.signInLink")}
        </LinkButton>
        .
      </Text>
    </>
  )

  function onSubmit(data: CreateAccountFormSchema) {
    mutate(data)
  }

  async function mutationFn(data: CreateAccountFormSchema) {
    return api.auth.signUp({
      username: data.email,
      password: data.password,
      options: {
        userAttributes: {
          email: data.email,
          phone_number: data.phone,
          given_name: data.firstName,
          family_name: data.lastName,
        },
        autoSignIn: true,
      },
    })
  }

  function onSuccess(result: SignUpOutput, variables: CreateAccountFormSchema) {
    if (result.nextStep?.signUpStep === "CONFIRM_SIGN_UP") {
      navigate({
        to: "/auth/verify",
        search: {
          email: variables.email,
          context: "create-account",
          qrLookupID,
          redirect,
        },
      })
    } else {
      // Account was created without email verification required
      if (redirect) {
        navigate({ to: redirect })
      } else if (qrLookupID) {
        navigate({ to: "/qr/$qrLookupID/welcome", params: { qrLookupID } })
      } else {
        navigate({ to: "/" })
      }
    }
  }

  function onError(err: Error, variables: CreateAccountFormSchema) {
    logMutationError(err, {
      flow: "create-account",
      email: variables.email,
      qrLookupID,
      additionalData: {
        firstName: variables.firstName,
        lastName: variables.lastName,
      },
    })
    handleAuthError(err, form, t)
  }
}
